import { Service } from "typedi";

const LOCK_NAME = "zaindari-controller-leader";

type LeaderListener = (isLeader: boolean) => void;

/**
 * Leader election backed by the Web Locks API.
 *
 * Only one controller across the same-origin BroadcastChannel can hold the
 * exclusive lock. When the tab dies, the browser releases it automatically
 * and another controller picks it up. Listeners are notified synchronously on
 * the transition.
 */
@Service()
export class LeaderElection {
    private isLeader = false;
    private releaseLock: (() => void) | null = null;
    private readonly listeners = new Set<LeaderListener>();

    start(): void {
        if (!("locks" in navigator)) {
            console.warn("[leader-election] navigator.locks unavailable; assuming sole leader");
            this.setLeader(true);
            return;
        }

        void navigator.locks.request(LOCK_NAME, { mode: "exclusive" }, async () => {
            this.setLeader(true);
            await new Promise<void>((resolve) => {
                this.releaseLock = resolve;
            });
            this.setLeader(false);
        }).catch((err) => {
            console.error("[leader-election] lock request failed", err);
            this.setLeader(false);
        });
    }

    stop(): void {
        this.releaseLock?.();
        this.releaseLock = null;
    }

    getIsLeader(): boolean {
        return this.isLeader;
    }

    onChange(listener: LeaderListener): () => void {
        this.listeners.add(listener);
        listener(this.isLeader);
        return () => this.listeners.delete(listener);
    }

    private setLeader(value: boolean): void {
        if (this.isLeader === value) return;
        this.isLeader = value;
        this.listeners.forEach((l) => {
            try { l(value); } catch (err) { console.error("[leader-election] listener error", err); }
        });
    }
}
